<template>
    <div class="rich-text-editor">
        <label v-if="label" :for="id" class="block text-sm font-medium text-gray-700 mb-2">
            {{ label }}
        </label>
        <div class="border border-gray-300 rounded-md overflow-hidden">
            <!-- CKEditor Component -->
            <ckeditor v-if="editor" v-model="editorData" :editor="editor" :config="editorConfig as any"
                :disabled="disabled" @ready="onEditorReady" @focus="onEditorFocus" @blur="onEditorBlur"
                @input="onEditorInput" />
            <!-- Loading state -->
            <div v-else-if="cloud.loading.value" class="p-4 text-center text-gray-500">
                <div class="animate-pulse">Loading CKEditor...</div>
            </div>
            <!-- Error fallback -->
            <div v-else-if="cloud.error.value" class="p-4 bg-yellow-50 border border-yellow-200 rounded">
                <p class="text-yellow-700 text-sm mb-2">CKEditor failed to load. Using fallback editor:</p>
                <textarea v-model="editorData" class="w-full p-3 border border-gray-300 rounded resize-none"
                    :style="{ height: `${height}px` }" placeholder="Enter your content here..."
                    @input="onFallbackInput" />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { Ckeditor, useCKEditorCloud } from '@ckeditor/ckeditor5-vue'
import axios from '@/axios'

// Define props
interface Props {
    modelValue?: string
    label?: string
    id?: string
    height?: number
    disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    modelValue: '',
    label: '',
    id: 'editor',
    height: 450,
    disabled: false
})

// Define emits
const emit = defineEmits<{
    'update:modelValue': [value: string]
    ready: [editor: unknown]
    focus: [event: unknown, editor: unknown]
    blur: [event: unknown, editor: unknown]
    input: [data: string, event: unknown, editor: unknown]
}>()

// Initialize CKEditor Cloud
// For licensing options:
// 1. Free open-source: Leave licenseKey empty (current setup)
// 2. Free account: Register at https://ckeditor.com and get a free license key
// 3. Paid account: Get premium features with a paid license key
const cloud = useCKEditorCloud({
    version: '45.2.0'
})

// Two-way data binding for editor content
const editorData = ref(props.modelValue)

// Watch for external changes to modelValue
watch(() => props.modelValue, (newValue) => {
    if (editorData.value !== newValue) {
        editorData.value = newValue
    }
})

// Watch for internal changes to editorData
watch(editorData, (newValue) => {
    emit('update:modelValue', newValue)
})

// Get the editor class from cloud data
const editor = computed(() => {
    if (!cloud.data.value) {
        return null
    }
    return cloud.data.value.CKEditor.ClassicEditor
})

// Custom upload adapter for image uploads
interface FileLoader {
    file: Promise<File>
}

interface UploadResponse {
    default: string
}

class MyUploadAdapter {
    private loader: FileLoader

    constructor(loader: FileLoader) {
        this.loader = loader
    } upload(): Promise<UploadResponse> {
        return this.loader.file
            .then((file: File) => new Promise<UploadResponse>((resolve, reject) => {                // Validate file type
                const allowedTypes = [
                    'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'video/mp4'
                ]
                if (!allowedTypes.includes(file.type)) {
                    reject('File type not supported. Please upload: JPEG, PNG, GIF, WebP images, or MP4 videos.')
                    return
                }

                // Validate file size (max 5MB for images, 25MB for mp4)
                let maxSize = 5 * 1024 * 1024 // 5MB default
                if (file.type === 'video/mp4') {
                    maxSize = 25 * 1024 * 1024 // 25MB for mp4
                }
                if (file.size > maxSize) {
                    reject(`File size too large. Maximum size is ${file.type === 'video/mp4' ? '25MB for videos' : '5MB for images'}.`)
                    return
                }

                const formData = new FormData()
                formData.append('image', file)
                formData.append('name', file.name || `editor_image_${Date.now()}`)
                formData.append('source', 'ckeditor') // Help backend identify the source                // Use the backend endpoint for CKEditor image uploads
                axios.post('/media/upload-image', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        // Add CSRF token if needed
                        // 'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
                    },
                    timeout: 30000 // 30 second timeout
                })
                    .then(response => {
                        console.log('Upload response:', response.data)

                        // Handle different response formats from Laravel
                        const imageUrl = response.data?.url ||
                            response.data?.secure_url ||
                            response.data?.path ||
                            response.data?.image_url ||
                            response.data?.data?.url

                        if (imageUrl) {
                            resolve({
                                default: imageUrl
                            })
                        } else {
                            console.error('No URL in response:', response.data)
                            reject('Upload failed - no URL returned from server')
                        }
                    })
                    .catch(error => {
                        console.error('Upload error:', error)

                        // Handle different error types
                        let errorMessage = 'Upload failed'
                        if (error.response?.status === 413) {
                            errorMessage = 'File too large for server'
                        } else if (error.response?.status === 422) {
                            errorMessage = error.response.data?.message || 'Validation error'
                        } else if (error.response?.status === 401) {
                            errorMessage = 'Authentication required'
                        } else if (error.response?.data?.message) {
                            errorMessage = error.response.data.message
                        } else if (error.message) {
                            errorMessage = error.message
                        }                        // For development: fallback to base64 if upload fails
                        if (import.meta.env.DEV) {
                            console.warn('Using base64 fallback for development')
                            const reader = new FileReader()
                            reader.onload = () => {
                                resolve({
                                    default: reader.result as string
                                })
                            }
                            reader.onerror = () => {
                                reject(`Upload failed: ${errorMessage}`)
                            }
                            reader.readAsDataURL(file)
                        } else {
                            reject(`Upload failed: ${errorMessage}`)
                        }
                    })
            }))
    }

    abort(): void {
        // Abort the upload process
    }
}

// Plugin function to register the upload adapter
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function MyCustomUploadAdapterPlugin(editor: any): void {
    editor.plugins.get('FileRepository').createUploadAdapter = (loader: FileLoader) => {
        return new MyUploadAdapter(loader)
    }
}

// Editor configuration
const editorConfig = computed(() => {
    if (!cloud.data.value) {
        return undefined
    }
    const {
        Essentials,
        Paragraph,
        Bold,
        Italic,
        Underline,
        Strikethrough,
        Code,
        Subscript,
        Superscript,
        Heading,
        Link,
        Image,
        ImageUpload,
        ImageToolbar,
        ImageCaption,
        ImageStyle,
        ImageResize,
        List,
        Indent,
        IndentBlock,
        Table,
        TableToolbar,
        BlockQuote,
        MediaEmbed,
        HorizontalLine,
        SourceEditing,
        Alignment,
        FontSize,
        FontFamily,
        FontColor,
        FontBackgroundColor,
        RemoveFormat,
        GeneralHtmlSupport
    } = cloud.data.value.CKEditor

    return {
        // For free use without premium features, you can omit the licenseKey
        // To use CKEditor Cloud with a free account, register at https://ckeditor.com
        // and replace 'YOUR_FREE_LICENSE_KEY' with your actual key
        licenseKey: 'eyJhbGciOiJFUzI1NiJ9.eyJleHAiOjE3ODIxNzI3OTksImp0aSI6IjRkYjk3MjQ4LTc4MTctNDUwYy05NjE3LWNmMjk4ZWJiMjAxMSIsInVzYWdlRW5kcG9pbnQiOiJodHRwczovL3Byb3h5LWV2ZW50LmNrZWRpdG9yLmNvbSIsImRpc3RyaWJ1dGlvbkNoYW5uZWwiOlsiY2xvdWQiLCJkcnVwYWwiXSwiZmVhdHVyZXMiOlsiRFJVUCIsIkUyUCIsIkUyVyJdLCJ2YyI6ImRlZjFhNTcxIn0.6foIkKYXkdVgy8e82AaeRMlVxL4aFjHxgNh05qoIxFQa02asnECR5mkG7enOUkLN9XJj_XhlGfR3Vti-WjAy1Q', // Leave empty for open source version, or add your free license key here
        language: 'sk', // Set Slovak language
        extraPlugins: [MyCustomUploadAdapterPlugin],
        plugins: [
            Essentials,
            Paragraph,
            Bold,
            Italic,
            Underline,
            Strikethrough,
            Code,
            Subscript,
            Superscript,
            Heading,
            Link,
            Image,
            ImageUpload,
            ImageToolbar,
            ImageCaption,
            ImageStyle,
            ImageResize,
            List,
            Indent,
            IndentBlock,
            Table,
            TableToolbar,
            BlockQuote,
            MediaEmbed,
            HorizontalLine,
            SourceEditing,
            Alignment,
            FontSize,
            FontFamily,
            FontColor,
            FontBackgroundColor,
            RemoveFormat,
            GeneralHtmlSupport
        ],
        toolbar: [
            'heading', '|',
            'fontSize', 'fontFamily', '|',
            'bold', 'italic', 'underline', 'strikethrough', '|',
            'fontColor', 'fontBackgroundColor', '|',
            'link', 'imageUpload', '|',
            'alignment', '|',
            'bulletedList', 'numberedList', '|',
            'outdent', 'indent', '|',
            'insertTable', 'blockQuote', 'horizontalLine', '|',
            'code', 'subscript', 'superscript', '|',
            'removeFormat', '|',
            'undo', 'redo', '|',
            'mediaEmbed', '|',
            'sourceEditing'
        ],
        image: {
            toolbar: [
                'imageTextAlternative', '|',
                'imageStyle:inline',
                'imageStyle:block',
                'imageStyle:side', '|',
                'resizeImage'
            ]
        },
        heading: {
            options: [
                { model: 'paragraph', title: 'Odstavec', class: 'ck-heading_paragraph' },
                { model: 'heading1', view: 'h1', title: 'Nadpis 1', class: 'ck-heading_heading1' },
                { model: 'heading2', view: 'h2', title: 'Nadpis 2', class: 'ck-heading_heading2' },
                { model: 'heading3', view: 'h3', title: 'Nadpis 3', class: 'ck-heading_heading3' },
                { model: 'heading4', view: 'h4', title: 'Nadpis 4', class: 'ck-heading_heading4' }
            ]
        },
        table: {
            contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells']
        },
        link: {
            decorators: {
                openInNewTab: {
                    mode: 'manual',
                    label: 'Otvoriť v novej karte',
                    attributes: {
                        target: '_blank',
                        rel: 'noopener noreferrer'
                    }
                }
            }
        },
        fontSize: {
            options: [
                'tiny',
                'small',
                'default',
                'big',
                'huge'
            ]
        },
        fontFamily: {
            options: [
                'default',
                'Arial, sans-serif',
                'Courier New, monospace',
                'Georgia, serif',
                'Lucida Sans Unicode, sans-serif',
                'Tahoma, sans-serif',
                'Times New Roman, serif',
                'Trebuchet MS, sans-serif',
                'Verdana, sans-serif'
            ]
        },
        fontColor: {
            columns: 6,
            colors: [
                { color: '#000000', label: 'Čierna' },
                { color: '#4d4d4d', label: 'Tmavo sivá' },
                { color: '#999999', label: 'Sivá' },
                { color: '#e6e6e6', label: 'Svetlo sivá' },
                { color: '#ffffff', label: 'Biela', hasBorder: true },
                { color: '#e64c4c', label: 'Červená' },
                { color: '#e67e22', label: 'Oranžová' },
                { color: '#f1c40f', label: 'Žltá' },
                { color: '#2ecc71', label: 'Zelená' },
                { color: '#3498db', label: 'Modrá' },
                { color: '#9b59b6', label: 'Fialová' }
            ]
        },
        fontBackgroundColor: {
            columns: 6,
            colors: [
                { color: '#000000', label: 'Čierna' },
                { color: '#4d4d4d', label: 'Tmavo sivá' },
                { color: '#999999', label: 'Sivá' },
                { color: '#e6e6e6', label: 'Svetlo sivá' },
                { color: '#ffffff', label: 'Biela', hasBorder: true },
                { color: '#e64c4c', label: 'Červená' },
                { color: '#e67e22', label: 'Oranžová' },
                { color: '#f1c40f', label: 'Žltá' },
                { color: '#2ecc71', label: 'Zelená' },
                { color: '#3498db', label: 'Modrá' },
                { color: '#9b59b6', label: 'Fialová' }
            ]
        },
        alignment: {
            options: ['left', 'center', 'right', 'justify']
        },
        htmlSupport: {
            allow: [
                {
                    name: /.*/,
                    attributes: true,
                    classes: true,
                    styles: true
                }
            ],
            disallow: [
                // Block inline event handlers (XSS prevention)
                {
                    name: /.*/,
                    attributes: /^on/i
                },
                // Block <script> tags
                { name: 'script' }
            ]
        }
    }
})

// Add custom button to CKEditor after ready
const onEditorReady = (editor: any) => {
    console.log('CKEditor is ready:', editor)

    // Set editor height to maintain consistent sizing
    if (props.height) {
        const editableElement = editor.ui.view.editable.element
        if (editableElement) {
            // Set both min-height and height for more stable sizing
            editableElement.style.minHeight = `${props.height}px`
            editableElement.style.height = `${props.height}px`
            editableElement.style.maxHeight = `${props.height * 2}px` // Allow some expansion
            editableElement.style.overflowY = 'auto' // Add scrollbar when content exceeds height
        }
    }

    emit('ready', editor)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const onEditorFocus = (event: any, editor: any) => {
    // Ensure height remains stable when editor gains focus
    if (props.height) {
        const editableElement = editor.ui.view.editable.element
        if (editableElement) {
            editableElement.style.height = `${props.height}px`
            editableElement.style.minHeight = `${props.height}px`
        }
    }
    emit('focus', event, editor)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const onEditorBlur = (event: any, editor: any) => {
    // Maintain height stability when editor loses focus
    if (props.height) {
        const editableElement = editor.ui.view.editable.element
        if (editableElement) {
            editableElement.style.height = `${props.height}px`
            editableElement.style.minHeight = `${props.height}px`
        }
    }
    emit('blur', event, editor)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const onEditorInput = (data: string, event: any, editor: any) => {
    emit('input', data, event, editor)
}

// Fallback textarea input handler
const onFallbackInput = (event: Event) => {
    const target = event.target as HTMLTextAreaElement
    const value = target.value
    editorData.value = value
    emit('input', value, event, null)
}
</script>

<style>
.rich-text-editor {
    width: 100%;
}

/* CKEditor 5 custom styles */
.rich-text-editor .ck-editor__editable {
    border-radius: 0;
    border: none;
    /* Prevent height changes on focus */
    transition: none !important;
}

.rich-text-editor .ck-editor__editable:focus {
    border: none;
    box-shadow: none;
    /* Maintain stable height during focus */
    resize: none;
}

.rich-text-editor .ck-toolbar {
    border-radius: 0;
    border: none;
    border-bottom: 1px solid #d1d5db;
}

/* Ensure dropdowns are properly displayed */
.rich-text-editor .ck-dropdown {
    position: relative;
    z-index: 9999;
}

.rich-text-editor .ck-dropdown__panel {
    z-index: 10000;
    max-height: 300px;
    overflow-y: auto;
}

/* Ensure heading dropdown has enough space */
.rich-text-editor .ck-heading-dropdown .ck-dropdown__panel {
    min-width: 200px;
    max-height: 400px;
}

/* Prevent container from clipping dropdowns */
.rich-text-editor {
    overflow: visible;
}

.rich-text-editor>div {
    overflow: visible;
}

/* Ensure stable editor content area */
.rich-text-editor .ck-content {
    /* Prevent automatic height adjustments */
    resize: none;
    /* Add proper typography styles for content elements */
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    font-size: 14px;
    line-height: 1.6;
    color: #374151;
}

/* Fix for CKEditor height stability */
.rich-text-editor .ck-editor__main>.ck-editor__editable {
    /* Remove any automatic height calculations */
    height: auto !important;
    min-height: inherit !important;
}

/* Typography styles for content elements inside the editor */
.rich-text-editor .ck-content h1,
.rich-text-editor .ck-content h2,
.rich-text-editor .ck-content h3,
.rich-text-editor .ck-content h4,
.rich-text-editor .ck-content h5,
.rich-text-editor .ck-content h6 {
    font-weight: 700;
    margin-top: 1.5em;
    margin-bottom: 0.5em;
    line-height: 1.25;
    color: #111827;
}

.rich-text-editor .ck-content h1 {
    font-size: 2.25em;
    /* 36px */
    margin-top: 0;
}

.rich-text-editor .ck-content h2 {
    font-size: 1.875em;
    /* 30px */
}

.rich-text-editor .ck-content h3 {
    font-size: 1.5em;
    /* 24px */
}

.rich-text-editor .ck-content h4 {
    font-size: 1.25em;
    /* 20px */
}

.rich-text-editor .ck-content h5 {
    font-size: 1.125em;
    /* 18px */
}

.rich-text-editor .ck-content h6 {
    font-size: 1em;
    /* 16px */
}

/* Paragraph styles */
.rich-text-editor .ck-content p {
    margin-bottom: 1em;
}

/* List styles */
.rich-text-editor .ck-content ul,
.rich-text-editor .ck-content ol {
    margin: 1em 0;
    padding-left: 2em;
}

.rich-text-editor .ck-content li {
    margin-bottom: 0.5em;
}

/* Blockquote styles */
.rich-text-editor .ck-content blockquote {
    margin: 1.5em 0;
    padding: 0 1em;
    border-left: 4px solid #d1d5db;
    color: #6b7280;
    font-style: italic;
}

/* Link styles */
.rich-text-editor .ck-content a {
    color: #059669;
    text-decoration: underline;
}

.rich-text-editor .ck-content a:hover {
    color: #047857;
}

/* Strong and emphasis */
.rich-text-editor .ck-content strong {
    font-weight: 700;
}

.rich-text-editor .ck-content em {
    font-style: italic;
}

/* Table styles */
.rich-text-editor .ck-content table {
    border-collapse: collapse;
    width: 100%;
    margin: 1em 0;
}

.rich-text-editor .ck-content table td,
.rich-text-editor .ck-content table th {
    border: 1px solid #d1d5db;
    padding: 0.75em;
    text-align: left;
}

.rich-text-editor .ck-content table th {
    background-color: #f9fafb;
    font-weight: 600;
}

/* Image styles */
.rich-text-editor .ck-content img {
    max-width: 100%;
    height: auto;
    margin: 1em 0;
    border-radius: 0.375rem;
}
</style>